'use client'

import * as React from 'react'
import { ReactNode, useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { CheckIcon, ChevronDownIcon, FileIcon, PackageIcon, XIcon } from 'lucide-react'
import { useProducts } from '@/hooks'
import { useMetaStore } from '@/store'
import { cn, getLocalizedValue, getResizedPictureUrl } from '@/lib/utils'
import { useDebounce } from '@/hooks'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import Image from 'next/image'

type ProductOption = {
  id: string
  code: string
  title: string
  categoryName?: string
  imageUrl?: string
}

type ProductComboboxCommonProps = {
  placeholder?: string
  disabled?: boolean
  className?: string
  error?: boolean
  clearable?: boolean
  excludeId?: string
  icon?: ReactNode
  id?: string
  currencyCode?: string
}

export type ProductComboboxProps = ProductComboboxCommonProps & {
  value?: string | string[]
  onValueChange?: (value: string | string[]) => void
  multiple?: boolean
}

export function ProductCombobox({
  value,
  onValueChange,
  placeholder,
  disabled = false,
  className,
  error: hasError = false,
  clearable = true,
  excludeId,
  icon,
  multiple,
  id,
  currencyCode
}: ProductComboboxProps) {
  const t = useTranslations()
  const locale = useLocale()
  const listId = React.useId()
  const { metaState } = useMetaStore()
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  // Fetch products based on a search query
  const { data: productsData, isLoading } = useProducts(
    {
      page: 1,
      size: 50,
      q: debouncedSearchQuery || undefined,
      excludeIds: excludeId ? [excludeId] : undefined
    },
    currencyCode
  )

  // Transform products to options with full data
  const options = useMemo<ProductOption[]>(() => {
    if (!productsData?.items) {
      return []
    }

    return productsData.items.map(product => ({
      id: product.id,
      code: product.code,
      title: getLocalizedValue(product, 'title', locale, defaultLocale),
      categoryName: product.category ? getLocalizedValue(product.category, 'name', locale, defaultLocale) : undefined,
      imageUrl: product.mainPicture ? getResizedPictureUrl(product.mainPicture.path, 64, 64) : undefined
    }))
  }, [productsData, locale, defaultLocale])

  const valueArray = multiple ? (value && typeof value === 'object' && 'length' in value ? value : []) : []
  const selectedOption = !multiple ? options.find(option => option.id === value) : null
  const selectedOptions = multiple ? options.filter(option => valueArray.includes(option.id)) : []
  const hasValue = multiple ? valueArray.length > 0 : value && value !== '' && value !== undefined

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          id={id}
          aria-controls={listId}
          aria-expanded={open}
          aria-invalid={hasError ? 'true' : undefined}
          disabled={disabled}
          className={cn(
            'border-input dark:bg-input/30 flex h-auto min-h-9 w-full cursor-pointer items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50',
            hasError
              ? 'border-destructive focus:border-destructive focus:ring-destructive/20 dark:focus:ring-destructive/40 focus:ring-[3px]'
              : 'focus:border-ring focus:ring-ring/50 focus:ring-[3px]',
            !value && 'text-muted-foreground',
            className
          )}>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {!multiple && (icon || <PackageIcon className="h-4 w-4 shrink-0" />)}
            {multiple ? (
              selectedOptions.length > 0 ? (
                <div className="flex flex-1 flex-wrap gap-1">
                  {selectedOptions.map(option => (
                    <div
                      key={option.id}
                      className="bg-muted flex items-center gap-1.5 rounded border px-2 py-0.5"
                      onClick={e => {
                        e.stopPropagation()
                      }}>
                      {option.imageUrl ? (
                        <Image
                          src={option.imageUrl}
                          alt=""
                          width="33"
                          height="32"
                          className="h-4 w-4 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <FileIcon className="text-muted-foreground h-3 w-3 shrink-0" />
                      )}
                      <span className="text-xs font-medium">{option.code}</span>
                      <XIcon
                        className="text-muted-foreground hover:text-foreground h-3 w-3 shrink-0 cursor-pointer"
                        onClick={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          const newValues = valueArray.filter(v => v !== option.id)
                          onValueChange?.(newValues)
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">{placeholder || t('select')}</span>
              )
            ) : selectedOption ? (
              <div className="flex min-w-0 items-center gap-2">
                {selectedOption.imageUrl ? (
                  <Image
                    src={selectedOption.imageUrl}
                    alt=""
                    width="33"
                    height="32"
                    className="h-6 w-6 shrink-0 rounded border object-cover"
                  />
                ) : (
                  <div className="bg-muted flex h-6 w-6 shrink-0 items-center justify-center rounded border">
                    <FileIcon className="text-muted-foreground h-4 w-4" />
                  </div>
                )}
                <div className="flex min-w-0 flex-col items-start">
                  <span className="truncate">{`${selectedOption.code} - ${selectedOption.title}`}</span>
                  {selectedOption.categoryName && (
                    <span className="text-muted-foreground truncate text-xs">{selectedOption.categoryName}</span>
                  )}
                </div>
              </div>
            ) : (
              placeholder || t('select')
            )}
          </div>
          <div className="flex items-center gap-1">
            {clearable && hasValue && (
              <div
                className="mr-1 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-100 text-gray-500 opacity-70 hover:opacity-100"
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (multiple) {
                    onValueChange?.([])
                  } else {
                    onValueChange?.('')
                  }
                }}
                onMouseDown={e => {
                  e.preventDefault()
                  e.stopPropagation()
                }}>
                <XIcon className="h-3 w-3" />
              </div>
            )}
            <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{
          minWidth: 'var(--radix-popper-anchor-width)',
          width: 'max-content'
        }}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('search_products')}
            value={search}
            onValueChange={value => {
              setSearch(value)
              setSearchQuery(value)
            }}
          />
          <CommandList id={listId} className="max-h-75">
            <CommandEmpty>{isLoading ? t('loading') : searchQuery ? t('not_found') : t('type_to_search')}</CommandEmpty>
            <CommandGroup>
              {options.map(option => (
                <CommandItem
                  key={option.id}
                  value={`${option.code} ${option.title}`}
                  onSelect={() => {
                    if (multiple) {
                      const currentValues = value && typeof value === 'object' && 'length' in value ? value : []
                      const newValues = currentValues.includes(option.id)
                        ? currentValues.filter(v => v !== option.id)
                        : [...currentValues, option.id]
                      onValueChange?.(newValues)
                    } else {
                      const newValue = option.id === value ? '' : option.id
                      onValueChange?.(newValue)
                      setOpen(false)
                    }
                  }}>
                  <CheckIcon
                    className={cn(
                      'mr-2 h-4 w-4',
                      multiple
                        ? valueArray.includes(option.id)
                          ? 'opacity-100'
                          : 'opacity-0'
                        : value === option.id
                          ? 'opacity-100'
                          : 'opacity-0'
                    )}
                  />
                  {option.imageUrl ? (
                    <Image
                      src={option.imageUrl}
                      alt=""
                      width="33"
                      height="32"
                      className="mr-2 h-8 w-8 shrink-0 rounded border object-cover"
                    />
                  ) : (
                    <div className="bg-muted mr-2 flex h-8 w-8 shrink-0 items-center justify-center rounded border">
                      <FileIcon className="text-muted-foreground h-5 w-5" />
                    </div>
                  )}
                  <div className="flex min-w-0 flex-col items-start">
                    <span className="truncate">{`${option.code} - ${option.title}`}</span>
                    {option.categoryName && (
                      <span className="text-muted-foreground truncate text-xs">{option.categoryName}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
